/**
 * DecisionFlow — Auth Context
 * Stage 8 Job A · A.2
 * M041 Phase 2 · S223 · May 2026
 * S280: loginWithSession() added for SSO callback flow.
 * S902: OI-S899-AUTH-STORAGE-GAP FIXED.
 *   - Boot probe now distinguishes 503 (transient DB blip — retry) from
 *     401/403 (genuine no session — clear user). Previously a 503 on
 *     apiMe() would set user=null and redirect to /login even though the
 *     httpOnly cookie was valid. Hard refresh = logout was this bug.
 *   - Boot probe retries up to 2× on 503 with 900ms backoff before giving up.
 *   - Keepalive interval: proactive POST /api/v1/auth/refresh every 14 min
 *     so token never expires during an active session. Cleared on logout.
 *   - Global AuthExpiredError handler wired into client.js interceptor so
 *     any screen's fetch can trigger the session-expired flow without
 *     requiring every screen to wrap in useApiCall().
 *
 * On app mount: tries GET /api/v1/auth/me. If cookie is valid → user set.
 * If not → user is null, protected routes redirect to /login.
 *
 * M041 Phase 2 boot sequence (runs inside login() after successful auth):
 *   1. POST /api/v1/auth/login         → session cookie set
 *   2. GET  /api/v1/tenant-context     → locale_code + locale fields
 *   3. GET  /api/v1/tenant-strings     → [ { namespace, key, value, locale_code } ]
 *   4. applyTenantOverrides()          → merge into live i18next bundle
 *   5. setUser() + setLocale()         → render authenticated shell
 *
 * SSO boot sequence (runs inside loginWithSession() after SSO callback):
 *   1. POST /api/v1/auth/sso/callback  → session cookie set (SsoCallback.jsx)
 *   2. GET  /api/v1/auth/me            → user profile (SsoCallback.jsx)
 *   3. loginWithSession(user)          → sets user state, triggers shell render
 *   Tenant i18n: best-effort, same as password login.
 */

import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiLogin, apiLogout, apiMe, apiGet, ApiError, AuthExpiredError, setGlobalAuthExpiredHandler } from "../api/client.js";
import { applyTenantOverrides } from "../i18n/index.js";

const AuthContext = createContext(null);

// S902 — enterprise keepalive constants.
// TOKEN_REFRESH_WINDOW_MS: how close to expiry before proactive refresh.
// ACTIVITY_CHECK_INTERVAL_MS: how often to check if refresh is needed.
// SESSION_WARNING_MS: warn user this far before token expiry (T-3min). OD-S903-A.
// JWT_TTL_MS: must match JWT_TTL_SECONDS in auth_service.py. Default 3600s (1hr).
// S903: IDLE_TIMEOUT_MS is no longer a module constant — it is loaded from
//   GET /api/v1/tenant-settings/session at boot and stored in idleTimeoutRef
//   so it reflects the tenant-configured value. Default 30 min if fetch fails.
const JWT_TTL_MS                  = parseInt(window.__DF_JWT_TTL_MS__ ?? "3600000", 10);
const TOKEN_REFRESH_WINDOW_MS     = 2 * 60 * 1000;   // refresh when < 2 min remaining
const SESSION_WARNING_MS          = 3 * 60 * 1000;   // warn at T-3min (OD-S903-A)
const ACTIVITY_CHECK_INTERVAL_MS  = 60 * 1000;        // check every 60s
const SESSION_IDLE_DEFAULT_MS     = 30 * 60 * 1000;   // fallback if tenant fetch fails

// Boot probe: retry up to 2× on transient 503 before treating as unauthenticated.
const BOOT_MAX_RETRIES    = 2;
const BOOT_RETRY_DELAY_MS = 900;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// S903 — Session Expiry Warning Modal
// ---------------------------------------------------------------------------
// Shown at T-3min before token expiry when the user has been idle.
// Does NOT close on outside click (NN-07 — modal close discipline).
// "Stay logged in" fires /auth/refresh immediately and dismisses.
// "Log out" fires the normal logout flow.
// No timer countdown displayed — keeps implementation simple and avoids
// a re-render loop. The message is accurate enough: "about 3 minutes".
// ---------------------------------------------------------------------------
function SessionExpiryModal({ onStay, onLogout }) {
  const [staying, setStaying] = useState(false);

  async function handleStay() {
    setStaying(true);
    await onStay();
    setStaying(false);
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div
        className="w-full max-w-sm rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-expiry-title"
      >
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[rgb(var(--color-warning))]/15">
            <svg className="h-5 w-5 text-[rgb(var(--color-warning))]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div>
            <h2 id="session-expiry-title" className="text-tok-base font-bold text-[rgb(var(--color-text))]">
              Your session is about to expire
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              You'll be logged out in about 3 minutes due to inactivity.
              Any unsaved work may be lost.
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleStay}
            disabled={staying}
            className="flex-1 rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2.5 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-60 transition-opacity"
          >
            {staying ? "Refreshing…" : "Stay logged in"}
          </button>
          <button
            onClick={onLogout}
            className="flex-1 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-2.5 text-tok-sm font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition-colors"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}

export function AuthProvider({ children }) {
  const [user, setUser]     = useState(null);
  const [booted, setBooted] = useState(false);
  const [error, setError]   = useState(null);
  const [locale, setLocale] = useState(null);

  const keepaliveRef       = useRef(null);
  const lastActivityRef    = useRef(Date.now());
  const tokenIssuedAtRef   = useRef(Date.now());
  // S903: tenant-configured idle timeout. Loaded at boot from /tenant-settings/session.
  // Stored in ref so keepalive interval always reads the live value without closure issues.
  const idleTimeoutRef     = useRef(SESSION_IDLE_DEFAULT_MS);
  // S903: OI-S902-JWT-TTL-INJECT — live JWT TTL loaded from /me response at boot+login.
  // Stored in ref so keepalive interval reads the live value. Falls back to JWT_TTL_MS constant.
  const jwtTtlRef          = useRef(JWT_TTL_MS);
  // S903: warning modal state. warningShownRef prevents re-firing every 60s once shown.
  const [sessionWarning, setSessionWarning] = useState(false);  // true = show modal
  const warningShownRef    = useRef(false);
  // S903: cross-tab logout sync via BroadcastChannel (OI-S902-CROSS-TAB-LOGOUT).
  // All tabs share the "df_auth" channel. Logout in one tab posts "logout";
  // other tabs receive it and clear their session immediately.
  const broadcastRef       = useRef(null);

  // S902: track user activity — any interaction resets the idle timer.
  useEffect(() => {
    const onActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousemove", "keydown", "pointerdown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onActivity));
  }, []);

  // S902: stopKeepalive defined BEFORE startKeepalive so startKeepalive's
  // closure can safely reference it. const is not hoisted — order matters.
  const stopKeepalive = useCallback(() => {
    if (keepaliveRef.current) {
      clearInterval(keepaliveRef.current);
      keepaliveRef.current = null;
    }
  }, []);


  // S903: cross-tab logout sync (OI-S902-CROSS-TAB-LOGOUT).
  // Opens a BroadcastChannel on mount. When another tab posts "logout",
  // this tab clears session state and redirects to /login immediately.
  // BroadcastChannel is not available in all environments (SSR, some mobile
  // browsers) — feature-detected before use. Channel is closed on unmount.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel("df_auth");
    broadcastRef.current = channel;

    channel.onmessage = (event) => {
      if (event.data === "logout") {
        stopKeepalive();
        warningShownRef.current = false;
        setSessionWarning(false);
        setUser(null);
        setLocale(null);
        if (window.location.pathname !== "/login") {
          window.location.replace("/login");
        }
      }
    };

    return () => {
      channel.close();
      broadcastRef.current = null;
    };
  }, [stopKeepalive]);
  // S902: activity-based keepalive. Uses keepaliveRef directly for the
  // 401-expiry path to avoid closure ordering issues with stopKeepalive.
  // Checks every 60s. Fires refresh only when user is active AND token
  // is near expiry. Dead sessions expire naturally — no wasted refreshes.
  const startKeepalive = useCallback(() => {
    if (keepaliveRef.current) clearInterval(keepaliveRef.current);
    tokenIssuedAtRef.current = Date.now();

    keepaliveRef.current = setInterval(async () => {
      const now          = Date.now();
      const idleDuration = now - lastActivityRef.current;
      const tokenAge     = now - tokenIssuedAtRef.current;
      const timeLeft     = jwtTtlRef.current - tokenAge;

      if (idleDuration > idleTimeoutRef.current) return;  // idle — let expire naturally

      // S903: T-3min warning. Fire once per idle cycle (warningShownRef gate).
      // Reset when user acts (activity listener) or refreshes successfully.
      if (timeLeft <= SESSION_WARNING_MS && !warningShownRef.current) {
        warningShownRef.current = true;
        setSessionWarning(true);
        return;  // don't refresh yet — user must choose Stay / Logout
      }

      if (timeLeft > TOKEN_REFRESH_WINDOW_MS)   return;  // plenty of time left

      try {
        const res = await fetch("/api/v1/auth/refresh", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });
        if (res.ok) {
          tokenIssuedAtRef.current = Date.now();
          warningShownRef.current  = false;  // S903: allow warning again on next idle cycle
          setSessionWarning(false);
        } else if (res.status === 401) {
          // Session truly expired — clear via ref directly (no closure issue)
          if (keepaliveRef.current) {
            clearInterval(keepaliveRef.current);
            keepaliveRef.current = null;
          }
          setUser(null);
          setLocale(null);
          if (window.location.pathname !== "/login") {
            window.location.replace("/login");
          }
        }
      } catch {
        // Network error — non-fatal, retry on next tick.
      }
    }, ACTIVITY_CHECK_INTERVAL_MS);
  }, [stopKeepalive]); // eslint-disable-line react-hooks/exhaustive-deps

  // S902: wire global AuthExpiredError handler into client.js so any
  // unauthenticated response from any screen clears session state without
  // requiring every screen to wrap calls in useApiCall().
  // The navigate call is deferred — AuthProvider may not have a router
  // context at the point setGlobalAuthExpiredHandler is called, so we
  // use window.location as the fallback. Screens using useApiCall() get
  // the navigate-based redirect; this catches everything else.
  useEffect(() => {
    setGlobalAuthExpiredHandler(() => {
      stopKeepalive();
      setUser(null);
      setLocale(null);
      // Defer to next tick so React state update completes before redirect.
      setTimeout(() => {
        if (window.location.pathname !== "/login") {
          window.location.replace("/login");
        }
      }, 0);
    });
    return () => setGlobalAuthExpiredHandler(null);
  }, [stopKeepalive]);

  // Stable ref to startKeepalive — lets boot probe call it without
  // adding startKeepalive to the probe's useEffect dep array.
  const startKeepaliveRef = useRef(startKeepalive);
  useEffect(() => { startKeepaliveRef.current = startKeepalive; }, [startKeepalive]);

  // S902: boot probe with 503-aware retry.
  // Distinguishes transient failure (503 → retry) from no session (401 → clear).
  // Previously: bare catch set user=null on ANY error including 503.
  useEffect(() => {
    let cancelled = false;

    async function probe() {
      let lastError = null;
      for (let attempt = 0; attempt <= BOOT_MAX_RETRIES; attempt++) {
        if (attempt > 0) await sleep(BOOT_RETRY_DELAY_MS);
        try {
          const me = await apiMe();
          if (!cancelled) {
            // S903: OI-S902-JWT-TTL-INJECT — read live JWT TTL from /me response.
            // Replaces window.__DF_JWT_TTT_MS__ which is never injected server-side.
            if (me?.jwt_ttl_ms && Number.isFinite(me.jwt_ttl_ms)) {
              jwtTtlRef.current = me.jwt_ttl_ms;
            }
            // S903: load tenant idle timeout. Best-effort — never blocks boot.
            try {
              const sessionCfg = await apiGet("/api/v1/tenant-settings/session");
              if (sessionCfg?.idle_timeout_minutes) {
                idleTimeoutRef.current = sessionCfg.idle_timeout_minutes * 60 * 1000;
              }
            } catch {
              // Non-fatal — keepalive uses SESSION_IDLE_DEFAULT_MS fallback.
            }
            setUser(me);
            startKeepaliveRef.current();
          }
          return; // success — done
        } catch (e) {
          lastError = e;
          // 503 = transient backend unavailability — retry.
          // Any other status (401, 403, network error) = break immediately.
          if (e instanceof ApiError && e.status === 503) {
            continue;
          }
          break;
        }
      }

      // All retries exhausted or non-retryable error.
      if (!cancelled) {
        // Only clear user on genuine auth failure (401/403).
        // On repeated 503 we leave user=null (unauthenticated) but don't
        // aggressively redirect — RouteGuard handles the redirect, and the
        // user can retry by navigating. This avoids a redirect loop when
        // the DB is briefly unavailable on startup.
        if (lastError instanceof ApiError && lastError.status === 503) {
          console.warn("[AuthContext] boot probe: repeated 503 — DB may be starting up. Treating as unauthenticated.");
        }
        setUser(null);
      }
    }

    probe().finally(() => {
      if (!cancelled) setBooted(true);
    });

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentionally empty deps — boot probe runs once on mount only.
  // startKeepalive is called inside probe() but is stable (useCallback with
  // stable stopKeepalive). Adding it to deps would re-run the probe on
  // keepalive recreation, causing user=null flashes on re-renders.

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const res = await apiLogin(email, password);

      try {
        const [contextData, stringsData] = await Promise.all([
          apiGet("/api/v1/tenant-context"),
          apiGet("/api/v1/tenant-strings"),
        ]);

        const localeCode = contextData?.locale?.locale_code ?? "en-US";
        const overrideList = stringsData?.data ?? [];
        const overrideMap = {};
        overrideList.forEach(({ namespace, key, value }) => {
          if (!overrideMap[namespace]) overrideMap[namespace] = {};
          overrideMap[namespace][key] = value;
        });
        applyTenantOverrides(localeCode, overrideMap);
        setLocale(localeCode);
      } catch (i18nErr) {
        console.warn("[AuthContext] tenant i18n fetch failed — using base strings:", i18nErr);
        setLocale("en-US");
      }

      // S903: OI-S902-JWT-TTL-INJECT — read live JWT TTL from login response expires_in.
      if (res?.expires_in && Number.isFinite(res.expires_in)) {
        jwtTtlRef.current = res.expires_in * 1000;
      }

      // S903: load tenant idle timeout. Best-effort — never blocks login.
      try {
        const sessionCfg = await apiGet("/api/v1/tenant-settings/session");
        if (sessionCfg?.idle_timeout_minutes) {
          idleTimeoutRef.current = sessionCfg.idle_timeout_minutes * 60 * 1000;
        }
      } catch { /* fallback to SESSION_IDLE_DEFAULT_MS */ }

      setUser(res.user);
      startKeepalive(); // S902: start keepalive after successful login
      return { ok: true, user: res.user };
    } catch (e) {
      setError(e);
      return { ok: false, error: e };
    }
  }, [startKeepalive]);

  const loginWithSession = useCallback(async (userProfile) => {
    setError(null);
    try {
      try {
        const [contextData, stringsData] = await Promise.all([
          apiGet("/api/v1/tenant-context"),
          apiGet("/api/v1/tenant-strings"),
        ]);

        const localeCode = contextData?.locale?.locale_code ?? "en-US";
        const overrideList = stringsData?.data ?? [];
        const overrideMap = {};
        overrideList.forEach(({ namespace, key, value }) => {
          if (!overrideMap[namespace]) overrideMap[namespace] = {};
          overrideMap[namespace][key] = value;
        });
        applyTenantOverrides(localeCode, overrideMap);
        setLocale(localeCode);
      } catch (i18nErr) {
        console.warn("[AuthContext] SSO tenant i18n fetch failed — using base strings:", i18nErr);
        setLocale("en-US");
      }

      setUser(userProfile);
      startKeepalive(); // S902: start keepalive after SSO login
      return { ok: true, user: userProfile };
    } catch (e) {
      setError(e);
      return { ok: false, error: e };
    }
  }, [startKeepalive]);

  const logout = useCallback(async () => {
    stopKeepalive();
    warningShownRef.current = false;
    setSessionWarning(false);
    // S903: notify other tabs before clearing local state (OI-S902-CROSS-TAB-LOGOUT).
    broadcastRef.current?.postMessage("logout");
    await apiLogout();
    setUser(null);
    setLocale(null);
  }, [stopKeepalive]);

  const handleSessionExpired = useCallback(() => {
    stopKeepalive();
    warningShownRef.current = false;
    setSessionWarning(false);
    setUser(null);
    setLocale(null);
  }, [stopKeepalive]);

  // S903: "Stay logged in" action from warning modal.
  // Fires /auth/refresh immediately, resets timer, dismisses modal.
  const stayLoggedIn = useCallback(async () => {
    setSessionWarning(false);
    warningShownRef.current = false;
    try {
      const res = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        tokenIssuedAtRef.current = Date.now();
      } else if (res.status === 401) {
        // Genuinely expired while warning was showing — clear session.
        stopKeepalive();
        setUser(null);
        setLocale(null);
        setTimeout(() => {
          if (window.location.pathname !== "/login") window.location.replace("/login");
        }, 0);
      }
    } catch {
      // Network error — non-fatal, modal is already dismissed, keepalive continues.
    }
  }, [stopKeepalive]);

  const value = {
    user,
    booted,
    error,
    locale,
    login,
    loginWithSession,
    logout,
    handleSessionExpired,
    stayLoggedIn,        // S903: "Stay logged in" from session expiry warning modal
    sessionWarning,      // S903: true when T-3min warning modal should show
    isCompAdmin: user?.user_type === "comp_admin",
    isFinance:   user?.user_type === "finance",
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* S903: Session expiry warning modal — shown at T-3min of idle timeout. */}
      {sessionWarning && (
        <SessionExpiryModal onStay={stayLoggedIn} onLogout={logout} />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function useApiCall() {
  const { handleSessionExpired } = useAuth();
  const navigate = useNavigate();

  return useCallback(async (fn) => {
    try {
      return await fn();
    } catch (e) {
      if (e instanceof AuthExpiredError) {
        handleSessionExpired();
        navigate("/login", { replace: true });
      }
      throw e;
    }
  }, [handleSessionExpired, navigate]);
}
